import { useState, useTransition } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../auth/AuthContext.jsx';
import { useClientData } from '../ClientLayout.jsx';
import { useSaveToast } from '../SaveToast.jsx';
import AddressAutocomplete from '../AddressAutocomplete.jsx';
import {
  encodeDebtorId,
} from '../../lib/debtorRecords.js';
import {
  debtorIdentityErrorMessage,
  normalizeDebtorIdentity,
} from '../../lib/debtorIdentity.js';
import {
  DEBTOR_COUNTRIES,
  coerceDebtorCountry,
  debtorPostalPlaceholder,
  debtorRegionLabel,
  debtorRegionMaxLength,
  debtorRegionPlaceholder,
  normalizeDebtorCountry,
} from '../../lib/debtorCountry.js';
import {
  isValidPostalForCountry,
  normalizePostalCode,
  postalCodeErrorMessage,
} from '../../lib/postalCode.js';
import { upsertDebtorProfile } from '../../lib/debtorProfiles.js';
import FieldTip from '../FieldTip.jsx';

const EMPTY = {
  Debtor_Name: '',
  Debtor_Company: '',
  Debtor_Country: 'US',
  Debtor_Street: '',
  Debtor_City: '',
  Debtor_State: '',
  Debtor_Postal_Code: '',
  Debtor_Email: '',
  External_Account_Number: '',
};

export default function DebtorCreatePage() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { refreshDebtors } = useClientData();
  const { flashSave } = useSaveToast();
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState('');
  const [pending, startTransition] = useTransition();

  function updateField(key, value) {
    setForm((prev) => ({ ...prev, [key]: value }));
  }

  function cancel() {
    navigate('/client/debtors');
  }

  function submit(e) {
    e.preventDefault();
    setError('');

    const identity = normalizeDebtorIdentity({
      contactName: form.Debtor_Name,
      company: form.Debtor_Company,
    });
    if (!identity.ok) {
      setError(debtorIdentityErrorMessage());
      return;
    }

    const postal = normalizePostalCode(form.Debtor_Postal_Code);
    const country = normalizeDebtorCountry(form.Debtor_Country, {
      postalCode: postal,
      state: form.Debtor_State,
    });
    if (postal && !isValidPostalForCountry(postal, country)) {
      setError(postalCodeErrorMessage(postal, country));
      return;
    }

    startTransition(() => {
      try {
        const saved = upsertDebtorProfile(user?.email, {
          Debtor_Name: identity.Debtor_Name,
          Debtor_Company: identity.Debtor_Company,
          Debtor_Country: country,
          Debtor_Street: String(form.Debtor_Street || '').trim(),
          Debtor_City: String(form.Debtor_City || '').trim(),
          Debtor_State: String(form.Debtor_State || '').trim(),
          Debtor_Postal_Code: postal,
          Debtor_Email: String(form.Debtor_Email || '').trim(),
          External_Account_Number: String(form.External_Account_Number || '').trim(),
        });
        refreshDebtors?.();
        flashSave('Debtor record created.');
        navigate(`/client/debtors/${encodeDebtorId(saved.id)}`, {
          state: { from: '/client/debtors' },
        });
      } catch (err) {
        setError(err?.message || 'Could not create debtor record.');
      }
    });
  }

  return (
    <div className="client-page debtor-create-page">
      <div className="section-head matter-detail-page-head">
        <div>
          <h1>Create Debtor</h1>
          <p>Add a debtor contact record. You can attach demands later.</p>
        </div>
        <div className="matter-detail-page-actions">
          <button type="button" className="btn-ghost" onClick={cancel}>
            Cancel
          </button>
          <div className="matter-detail-page-save">
            <button
              type="submit"
              form="debtor-create-form"
              className="btn-primary"
              disabled={pending}
            >
              {pending ? 'Saving…' : 'Save Debtor Record'}
            </button>
          </div>
        </div>
      </div>

      <form id="debtor-create-form" className="matter-detail-page-main" onSubmit={submit}>
        <section className="detail-section">
          <h3>Details</h3>
          {error ? <p className="form-error">{error}</p> : null}
          <div className="settings-grid matter-edit-grid">
            <label className="field">
              <span>Company</span>
              <input
                type="text"
                value={form.Debtor_Company}
                onChange={(e) => updateField('Debtor_Company', e.target.value)}
                placeholder="Acme LLC"
                autoComplete="organization"
              />
            </label>
            <label className="field">
              <span>Contact name</span>
              <input
                type="text"
                value={form.Debtor_Name}
                onChange={(e) => updateField('Debtor_Name', e.target.value)}
                placeholder="If known"
                autoComplete="name"
              />
            </label>
            <label className="field">
              <span className="field-label-with-tip">
                Account #
                <FieldTip
                  text="Your ID for this customer in your system. Demands that share this Account # consolidate into one debtor record."
                  label="Account #"
                />
              </span>
              <input
                type="text"
                value={form.External_Account_Number}
                onChange={(e) => updateField('External_Account_Number', e.target.value)}
                placeholder="If known"
              />
            </label>
            <label className="field field-span-2">
              <span>Street</span>
              <AddressAutocomplete
                value={form.Debtor_Street}
                onChange={(next) => updateField('Debtor_Street', next)}
                country={form.Debtor_Country}
                onAddressSelect={(address) => {
                  const nextCountry = coerceDebtorCountry(address.country);
                  setForm((prev) => ({
                    ...prev,
                    Debtor_Street: address.street || prev.Debtor_Street,
                    Debtor_City: address.city || prev.Debtor_City,
                    Debtor_State: address.state || prev.Debtor_State,
                    Debtor_Postal_Code: address.postalCode || prev.Debtor_Postal_Code,
                    Debtor_Country: nextCountry || prev.Debtor_Country,
                  }));
                }}
                placeholder="123 Main St"
              />
            </label>
            <label className="field">
              <span>City</span>
              <input
                type="text"
                value={form.Debtor_City}
                onChange={(e) => updateField('Debtor_City', e.target.value)}
              />
            </label>
            <label className="field">
              <span>{debtorRegionLabel(form.Debtor_Country)}</span>
              <input
                type="text"
                value={form.Debtor_State}
                onChange={(e) => {
                  const max = debtorRegionMaxLength(form.Debtor_Country);
                  const next =
                    max === 2
                      ? e.target.value.toUpperCase().slice(0, 2)
                      : e.target.value;
                  updateField('Debtor_State', next);
                }}
                maxLength={debtorRegionMaxLength(form.Debtor_Country)}
                placeholder={debtorRegionPlaceholder(form.Debtor_Country)}
              />
            </label>
            <label className="field">
              <span>Postal code</span>
              <input
                type="text"
                value={form.Debtor_Postal_Code}
                onChange={(e) => updateField('Debtor_Postal_Code', e.target.value)}
                placeholder={debtorPostalPlaceholder(form.Debtor_Country)}
              />
            </label>
            <label className="field">
              <span>Country</span>
              <select
                value={form.Debtor_Country}
                onChange={(e) => updateField('Debtor_Country', e.target.value)}
              >
                {DEBTOR_COUNTRIES.map((c) => (
                  <option key={c.value} value={c.value}>
                    {c.label}
                  </option>
                ))}
              </select>
            </label>
            <label className="field field-span-2">
              <span>Email</span>
              <input
                type="email"
                value={form.Debtor_Email}
                onChange={(e) => updateField('Debtor_Email', e.target.value)}
                placeholder="If known"
              />
            </label>
          </div>
        </section>
      </form>
    </div>
  );
}
